import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { CoinLogInterface } from "src/store/callrecorder/coinLogsSlice";
import { fetchCoinLogs } from "src/actions/callrecorder/coinLogsActions";
import { queryParams } from "src/helpers/UrlHelpers";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";

import { Tag } from "antd";

interface ListTableInterface {
  filterParams: object;
  limit?: number;
}

const _ListTable = ({ filterParams, limit = 25 }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const currentPage = queryParams.getInt(searchParams, 'page') || 1;
  const ids = useAppSelector(state => state.callrecorder.coinLogs.ids);
  const entities = useAppSelector(state => state.callrecorder.coinLogs.entities);
  const count = useAppSelector(state => state.callrecorder.coinLogs.count);
  const data = ids ? ids.map(id => entities[id]) : [];

  const urlOrderBy = searchParams.get('order_by') || '';
  const urlOrder = searchParams.get('order') || '';

  const params = useMemo(() => ({
    limit,
    offset: (currentPage - 1) * limit,
    ...filterParams,
    ...(urlOrderBy ? { order_by: urlOrderBy, order: urlOrder } : {})
  }), [limit, currentPage, filterParams, urlOrderBy, urlOrder]);

  useEffect(() => {
    dispatch(fetchCoinLogs(params));
  }, [params, dispatch]);

  const typeColor = (typeName: string | null) => {
    switch (typeName) {
      case 'buy': return 'blue';
      case 'buy_number': return 'cyan';
      case 'buy_premium_number': return 'geekblue';
      case 'buy_provider_number': return 'purple';
      case 'buy_extra_sms': return 'magenta';
      case 'phone_number_renewal': return 'green';
      case 'daily_bonus': return 'gold';
      case 'weekly_bonus': return 'orange';
      case 'remove_ads': return 'volcano';
      case 'save_voice': return 'lime';
      case 'send_sms': return 'processing';
      case 'incoming_sms_overlimit': return 'warning';
      case 'sms_one_hour':
      case 'sms_one_day':
      case 'sms_watch_video': return 'default';
      case 'reset_spam': return 'error';
      case 'call_verification': return 'success';
      case 'in_app_purchase': return '#108ee9';
      default: return 'default';
    }
  };

  const columns: ColumnsType<CoinLogInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id }) => <small>{`#${id}`}</small>,
      width: 60
    },

    {
      key: 'type_name',
      title: 'Type',
      render: (_, { type_name }) => type_name ? <Tag color={typeColor(type_name)}>{type_name}</Tag> : '—',
      width: 160
    },

    {
      key: 'consume',
      title: 'Direction',
      render: (_, { consume }) => <Tag color={consume ? 'error' : 'success'}>{consume ? 'Consume' : 'Supply'}</Tag>,
      width: 90
    },

    {
      key: 'amount',
      title: 'Amount',
      render: (_, { amount }) => amount ? <small>{amount}</small> : '—',
      width: 80
    },

    {
      key: 'user_number_id',
      title: 'User Number',
      render: (_, { user_number_id }) => user_number_id ? (
        <OptionalLink link={callrecorderUrls.userNumberPath(user_number_id)}>
          <small>{`#${user_number_id}`}</small>
        </OptionalLink>
      ) : '—',
      width: 90
    },

    {
      key: 'productid',
      title: 'Product ID',
      render: (_, { productid }) => productid ? <small>{productid}</small> : '—',
      width: 120
    },

    {
      key: 'original_transaction_id',
      title: 'Transaction',
      render: (_, { original_transaction_id }) => original_transaction_id ? <small>{original_transaction_id}</small> : '—',
      width: 150
    },

    {
      key: 'created_at',
      title: 'Created',
      render: (_, { created_at }) => <small>{formattedDate(created_at).dateTime}</small>,
      width: 125
    },
  ];

  return (
    <div>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        sort_keys={['id', 'amount', 'created_at']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
